/**
 * HA-619 fire-time print warnings.
 *
 * Two outcomes the operator has to tell apart, because the recovery differs:
 * `NoPrinters` means nothing will ever print that ticket, so the kitchen never sees it unless
 * someone walks it over; `Unreachable` means the broker is holding the job and it prints when a
 * printer reconnects. Hence a modal for the first and a banner for the second (D-HA-1 D4).
 */
import type { Order } from "../types";
import type { RejectedStockLine } from "./stock-warnings";

export type SendWarningKind = "NoPrinters" | "Unreachable";

export interface SendWarning {
	queueId: string;
	queueName: string;
	kind: SendWarningKind;
}

/**
 * Both send paths now answer with the post-append order as well as the warnings — the order is
 * what the screen merges into local state, and it used to come back as a bare aggregate.
 */
export interface SendToKitchenResponse {
	order: Order;
	itemIds: string[];
	warnings: SendWarning[];
	/** HA-588 — lines refused on stock grounds. The rest of the order still sent. */
	stockRejections: RejectedStockLine[];
}

export interface CommitCourseFireResponse {
	order: Order;
	courseId: string;
	itemIds: string[];
	warnings: SendWarning[];
}

export const SEND_SUCCESS = "Sent to kitchen";

/** Warnings that must be acknowledged — the ticket is not going to print at all. */
export const blockingWarnings = (warnings: SendWarning[]): SendWarning[] =>
	warnings.filter((w) => w.kind === "NoPrinters");

/** Warnings the operator should see but need not act on — the ticket is queued, not lost. */
export const delayedWarnings = (warnings: SendWarning[]): SendWarning[] =>
	warnings.filter((w) => w.kind === "Unreachable");

/**
 * The queue is always named: "a queue has no printer" is not actionable when a course fires to
 * three of them (AC5).
 */
export function describeSendWarning(warning: SendWarning): string {
	return warning.kind === "NoPrinters"
		? `${warning.queueName} has no printer — this ticket won't print`
		: `No printer is online for ${warning.queueName} — queued, will print on reconnect`;
}

/** The success toast is suppressed whenever anything warned, so it can't contradict the warning. */
export const shouldShowSendSuccess = (warnings: SendWarning[]): boolean =>
	warnings.length === 0;
